import Client from "@/services/client";
import ClientNotes from "@/services/client-notes";
import Vue from "vue";

import CLIENT from "@/constants/clients";

const state = {
    client_details: {},
    client_id: null,
    editMode: false,
    editDetails: null,
    popupNotes: {
        show: false,
        list: [],
        key: 0
    },
    newDetails: defaultNewClientDetails(),
    newDetailsFormKey: 0, // used for re-initiate component,
    statusActive: true,
    clients: [],
    editDirty: null,
    contactDetailsFilter: CLIENT.CONTACT_DETAIL_TYPES.ALL
};

const getters = {
    clientDetails: state => {
        return state.client_details;
    },
    clientId: state => {
        return state.client_id;
    },
    editMode: state => {
        return state.editMode;
    },
    editDetails: state => {
        return state.editDetails;
    },
    newDetails: state => {
        return state.newDetails;
    },
    statusActive: state => {
        return state.statusActive;
    },
    popupKey: state => {
        return state.popupNotes.key;
    },
    isDirty: state => {
        return state.editDirty;
    }
};

const actions = {
    async saveNewClient({ state }, payload = {}) {
        const details = state.newDetails;
        const { firstname, lastname, canSave, email, mobile } = details;

        if (firstname === "") {
            throw new Error("First Name is required");
        }

        if (lastname === "") {
            throw new Error("Last Name is required");
        }

        // if mobile app extensions is enabled, email OR phone is required
        if (payload.isMobileAppExtensionEnabled) {
            if (mobile === "" && email === "") {
                throw new Error("CLIENT_MOBILE_APP_REQUIRES_EMAIL_PHONE");
            }
        }

        if (!canSave) {
            throw new Error("Error saving data");
        }

        return await Client.saveNewClient(details);
    },
    async loadPopupNotes({ commit }, clientId) {
        const res = await ClientNotes.getPopupNotes(clientId);

        // sort by index
        let list = res.resource.sort((a, b) => a.index - b.index);

        if (res.message.code === 200 && list.length > 0) {
            commit("showPopupNotes", list);
        } else {
            commit("clearPopupNotes"); // hide and clear list
        }
    },
    refreshSortPopupNotes({ commit }, list = []) {
        if (list.length > 0) {
            commit("showPopupNotes", list);
        }
    },
    getClientDetails({ commit, dispatch }, payload) {
        return Client.getClient(payload.id, payload.encoded, payload.index)
            .then(response => {
                if (
                    (payload.encoded === 0 || payload.encoded === "0") &&
                    !payload.index
                ) {
                    // change url from raw id to encoded id, without refreshing or reloading
                    history.replaceState(
                        {},
                        "",
                        `/view/client/profile/${response.resource.encoded_id}`
                    );
                }

                if (response.message.code == 200) {
                    commit("setClientDetails", response.resource);
                    commit("setClientId", response.resource.id);
                    commit("setEditDetails", _.cloneDeep(response.resource));
                    dispatch("loadPopupNotes", response.resource.id);

                    return true;
                }

                return false;
            })
            .catch(error => error.data);
    },
    updateClientDetails({ state }, payload) {
        let data = state.editDetails;
        let referrerType = "actor";
        let reminders = [];
        let exempts = [];

        data.selectedReminders.forEach(element => {
            reminders.push(element.value);
        });

        data.selectedExemptions.forEach(element => {
            exempts.push(element.id);
        });

        if (data.selectedReferrer) {
            if (data.selectedReferrer.type == 1) referrerType = "business";
            if (data.selectedReferrer.type == 2) referrerType = "campaign";
        }

        let params = {
            edit: true,
            is_google: true,
            id: data.id,
            sms_enabled: payload.smsEnabled,
            current_status: state.client_details.status.id,
            status_option: payload.balanceAction,
            refund_type: payload.refundType,
            county: data.county,
            firstname: data.firstname,
            lastname: data.lastname,
            address: data.address,
            city: data.city,
            state: data.state,
            zipcode: data.zipcode,
            country: data.country,
            birthdate: data.birthdate,
            home_country_code: data.home_country_code,
            home: data.home,
            mobile_country_code: data.mobile_country_code,
            mobile: data.mobile,
            work_country_code: data.work_country_code,
            work: data.work,
            fax_country_code: data.fax_country_code,
            fax: data.fax,
            email: data.email,
            reminder_options: reminders,
            exempt_options: exempts,
            driver_license: data.driver_license,
            referrer: data.selectedReferrer ? data.selectedReferrer.label : "",
            referrer_id: data.selectedReferrer
                ? "[" + referrerType + "]" + data.selectedReferrer.id
                : "",
            add_new: 0,
            payment_term: data.selectedPaymentTerm,
            status: data.selectedStatus,
            discounts: data.selectedDiscount,
            discount_ids: state.client_details.discount
                ? state.client_details.discount.id
                : "",
            is_record_sharing: data.isRecordSharing,
            home_location: data.homeLocation ? data.homeLocation : null,
            ori_home_location: data.oriHomeLocation
                ? data.oriHomeLocation
                : null
        };

        // ****************************************
        // * confirm remove reminder_type param
        // ****************************************

        return Client.updateClient(params)
            .then(response => {
                return response;
            })
            .catch(error => error.data);
    }
};

const mutations = {
    setDirty(state, isDirty) {
        state.editDirty = isDirty;
    },
    setClientDetails(state, clientDetails) {
        state.client_details = clientDetails;
        state.statusActive = clientDetails.status
            ? clientDetails.status.id == 1
            : true;
    },
    setClientDetailPhoneNumber(state, phoneNumber) {
        state.client_details.mobile = phoneNumber;
    },
    setClientId(state, clientId) {
        state.client_id = clientId;
    },
    setSelectedClient(state, client) {
        // TODO : not in use, to be removed ?
        state.client_details = client;
        state.client_id = client.id;
    },
    setEditMode(state, editMode) {
        state.editMode = editMode;
    },
    setEditDetails(state, editDetails) {
        state.editDetails = editDetails;
    },
    setEditDetailsField(state, field) {
        Vue.set(state.editDetails, field.key, field.value);
    },
    showPopupNotes(state, list) {
        state.popupNotes.list = list;
        state.popupNotes.show = true;
    },
    clearPopupNotes(state) {
        state.popupNotes.show = false;
        state.popupNotes.list = [];
    },
    hidePopupNotes(state) {
        state.popupNotes.show = false;
    },
    setNewDetails(state, modifiedDetails) {
        state.newDetails = {
            ...state.newDetails,
            ...modifiedDetails
        };
    },
    clearNewDetails(state) {
        state.newDetails = defaultNewClientDetails();
        state.newDetailsFormKey++;
    },
    updatePopupKey(state) {
        state.popupNotes.key++;
    },
    addClients(state, client) {
        state.clients.add(client);
    },
    setContactDetailsFilter(state, filter) {
        state.contactDetailsFilter = filter;
    },
    resetContactDetailsFilter(state) {
        state.contactDetailsFilter = CLIENT.CONTACT_DETAIL_TYPES.ALL;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};

function defaultNewClientDetails() {
    return {
        firstname: "",
        lastname: "",
        address: "",
        city: "",
        state: "",
        zipcode: "",
        country: "",
        county: "",
        birthdate: "",
        home_country_code: "",
        home: "",
        mobile_country_code: "",
        mobile: "",
        work_country_code: "",
        work: "",
        fax_country_code: "",
        fax: "",
        email: "",
        reminder_options: [],
        driver_license: "",
        referredBy: null,
        balance: "0.00",
        payment_term: "1",
        exempt_options: [],
        status: 1,
        discounts: "",
        id: "",
        group_insert: false
    };
}
